import * as React from 'react';
import PlatformSettingsLayout from '@/components/settings/platform/PlatformSettingsLayoutV2';
import { Panel } from '@ampm-7en-ai/ui/components/Panel';
import { Field, Input } from '@ampm-7en-ai/ui/components/Input';
import { Textarea } from '@ampm-7en-ai/ui/components/Textarea';
import { Select } from '@ampm-7en-ai/ui/components/Select';
import { Button } from '@ampm-7en-ai/ui/components/Button';
import { ToggleRow } from '@ampm-7en-ai/ui/components/ToggleRow';
import { SegmentedControl, type SegmentedOption } from '@ampm-7en-ai/ui/components/SegmentedControl';
import { Pill } from '@ampm-7en-ai/ui/components/StatusBadge';
import { Skeleton } from '@ampm-7en-ai/ui/components/Skeleton';
import { useToast } from '@/hooks/use-toast';
import { useEmailTemplate, useEmailTemplateTypes, useUpdateEmailTemplate } from '@/hooks/useEmailTemplates';

/**
 * What the platform sends, and what it says.
 *
 * One template at a time: pick which email, write it, and switch it off if it
 * shouldn't go out. The placeholders are inserted by clicking rather than
 * typed from memory, since a mistyped one silently sends as literal text.
 */

const FORMATS: SegmentedOption<'html' | 'mjml'>[] = [
  { value: 'html', label: 'HTML' },
  { value: 'mjml', label: 'MJML' },
];

export default function CustomizationSettingsV2() {
  const { toast } = useToast();

  const { data: types, isLoading: loadingTypes } = useEmailTemplateTypes();
  const [templateType, setTemplateType] = React.useState<string | null>(null);
  const { data: template, isLoading: loadingTemplate } = useEmailTemplate(templateType);
  const saveTemplate = useUpdateEmailTemplate();

  const [name, setName] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [content, setContent] = React.useState('');
  const [format, setFormat] = React.useState<'html' | 'mjml'>('html');
  const [active, setActive] = React.useState(true);
  const contentRef = React.useRef<HTMLTextAreaElement>(null);
  const allowedPlaceholders = loadingTemplate ? [] : (template?.allowed_placeholders ?? []);

  React.useEffect(() => {
    if (types && types.length > 0 && !templateType) setTemplateType(types[0].type);
  }, [types, templateType]);

  React.useEffect(() => {
    if (!template) return;
    setName(template.name ?? '');
    setSubject(template.subject ?? '');
    setContent(template.content ?? '');
    setFormat(template.format?.toLowerCase() === 'mjml' ? 'mjml' : 'html');
    setActive(template.is_active ?? true);
  }, [template]);

  /** Drops a placeholder where the cursor is, rather than at the end. */
  const insert = (placeholder: string) => {
    const field = contentRef.current;
    if (!field) {
      setContent((current) => current + placeholder);
      return;
    }
    const { selectionStart: start, selectionEnd: end } = field;
    setContent(content.slice(0, start) + placeholder + content.slice(end));
    window.setTimeout(() => {
      field.focus();
      field.setSelectionRange(start + placeholder.length, start + placeholder.length);
    }, 0);
  };

  const save = async () => {
    if (!templateType) return;
    try {
      await saveTemplate.mutateAsync({
        templateType,
        payload: { name, subject, content, is_active: active, format },
      });
      toast({ title: `${name || 'Template'} saved` });
    } catch (failure) {
      toast({
        title: 'That didn’t save',
        description: failure instanceof Error ? failure.message : 'The template wasn’t saved.',
        variant: 'destructive',
      });
    }
  };

  return (
    <PlatformSettingsLayout title="Emails" description="What the platform sends, and what it says.">

      <Panel padding="md">
        {loadingTypes ? (
          <div className="space-y-4">
            <Skeleton className="h-[38px] w-64 rounded-ui-md" />
            <Skeleton className="h-48 w-full rounded-ui-md" />
          </div>
        ) : (types ?? []).length === 0 ? (
          <p className="text-ui-sm text-ui-fg-muted">No email templates are defined yet.</p>
        ) : (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Template" htmlFor="template-type">
                <Select
                  id="template-type"
                  options={(types ?? []).map((one) => ({ value: one.type, label: one.name ?? one.type }))}
                  value={templateType ?? ''}
                  onValueChange={setTemplateType}
                />
              </Field>
              <Field label="Name" htmlFor="template-name">
                <Input
                  id="template-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={loadingTemplate}
                />
              </Field>
            </div>

            <Field label="Subject" htmlFor="template-subject" hint="Placeholders work here too.">
              <Input
                id="template-subject"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                disabled={loadingTemplate}
              />
            </Field>

            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="text-ui-sm font-medium text-ui-fg">Body</span>
                <SegmentedControl size="sm" aria-label="Format" options={FORMATS} value={format} onValueChange={setFormat} />
              </div>
              <Textarea
                ref={contentRef}
                id="template-content"
                rows={14}
                value={content}
                onChange={(event) => setContent(event.target.value)}
                disabled={loadingTemplate}
                className="font-ui-mono text-ui-sm"
              />
              {allowedPlaceholders.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="text-ui-xs text-ui-fg-subtle">Insert:</span>
                  {allowedPlaceholders.map((placeholder) => (
                    <button
                      key={placeholder}
                      type="button"
                      onClick={() => insert(placeholder)}
                      className="rounded-full border border-ui-border px-2 py-0.5 font-ui-mono text-ui-xs text-ui-fg-muted transition-colors hover:border-ui-accent hover:text-ui-accent-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring"
                    >
                      {placeholder}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <ToggleRow
              label="Send this one"
              description="Switched off, the platform skips this email entirely."
              checked={active}
              onCheckedChange={setActive}
            />

            <div className="flex items-center justify-end gap-3">
              {template && !active && <Pill tone="warning">Not being sent</Pill>}
              <Button
                variant="primary"
                onClick={() => void save()}
                disabled={saveTemplate.isPending || loadingTemplate || !templateType}
              >
                {saveTemplate.isPending ? 'Saving…' : 'Save template'}
              </Button>
            </div>
          </div>
        )}
      </Panel>

    </PlatformSettingsLayout>
  );
}
